import { resolveOptimalAudioMimeType, MimeTypeConfig } from './mime-negotiator';
import { validateAudioBlob, AudioValidationResult } from './audio-validator';

export interface RecordedAudioSession {
  blob: Blob;
  validation: AudioValidationResult;
  mimeType: string;
  extension: MimeTypeConfig['extension'];
}

export class MediaRecorderService {
  private mediaStream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private recordedChunks: Blob[] = [];
  private mimeConfig: MimeTypeConfig = { mimeType: '', extension: 'm4a', isIosPlayable: true };

  /**
   * Starts live audio recording using voice-optimized microphone constraints.
   */
  async startRecording(timesliceMs: number = 250): Promise<MimeTypeConfig> {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error('navigator.mediaDevices.getUserMedia is not supported in this browser environment.');
    }

    // 1. Microphone constraints
    const constraints: MediaStreamConstraints = {
      audio: {
        channelCount: 1,
        sampleRate: 44100,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: false,
    };

    this.mediaStream = await navigator.mediaDevices.getUserMedia(constraints);

    // 2. Negotiate optimal MIME type
    this.mimeConfig = resolveOptimalAudioMimeType();
    const options: MediaRecorderOptions = {};
    if (this.mimeConfig.mimeType) {
      options.mimeType = this.mimeConfig.mimeType;
    }

    try {
      this.mediaRecorder = new MediaRecorder(this.mediaStream, options);
    } catch {
      // If specifying mimeType throws (e.g. strict Safari or edge-case platform), create default
      this.mediaRecorder = new MediaRecorder(this.mediaStream);
    }

    this.recordedChunks = [];

    this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
      if (event.data && event.data.size > 0) {
        this.recordedChunks.push(event.data);
      }
    };

    this.mediaRecorder.start(timesliceMs);
    return this.mimeConfig;
  }

  /**
   * Pauses an active recording session.
   */
  pause(): void {
    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
      this.mediaRecorder.pause();
    }
  }

  /**
   * Resumes a paused recording session.
   */
  resume(): void {
    if (this.mediaRecorder && this.mediaRecorder.state === 'paused') {
      this.mediaRecorder.resume();
    }
  }

  /**
   * Stops recording, releases hardware microphone tracks, and returns validated audio session.
   */
  async stopRecording(): Promise<RecordedAudioSession> {
    return new Promise((resolve, reject) => {
      if (!this.mediaRecorder) {
        this.cleanupStream();
        reject(new Error('No active MediaRecorder found to stop.'));
        return;
      }

      this.mediaRecorder.onstop = async () => {
        try {
          const type = this.mimeConfig.mimeType || this.mediaRecorder?.mimeType || 'audio/mp4';
          const blob = new Blob(this.recordedChunks, { type });

          // Release hardware tracks
          this.cleanupStream();

          // Validate recorded audio
          const validation = await validateAudioBlob(blob);

          resolve({
            blob,
            validation,
            mimeType: type,
            extension: this.mimeConfig.extension,
          });
        } catch (err) {
          this.cleanupStream();
          reject(err);
        }
      };

      if (this.mediaRecorder.state !== 'inactive') {
        this.mediaRecorder.stop();
      } else {
        // Already stopped
        this.cleanupStream();
        const type = this.mimeConfig.mimeType || 'audio/mp4';
        const blob = new Blob(this.recordedChunks, { type });
        validateAudioBlob(blob).then((validation) => {
          resolve({
            blob,
            validation,
            mimeType: type,
            extension: this.mimeConfig.extension,
          });
        }).catch(reject);
      }
    });
  }

  /**
   * Aborts recording and discards chunks.
   */
  cancel(): void {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try {
        this.mediaRecorder.stop();
      } catch {
        // Ignore
      }
    }
    this.cleanupStream();
    this.recordedChunks = [];
  }

  private cleanupStream(): void {
    if (this.mediaStream) {
      try {
        this.mediaStream.getTracks().forEach((track) => track.stop());
      } catch {
        // Ignore
      }
      this.mediaStream = null;
    }
    this.mediaRecorder = null;
  }
}
